import React from 'react'
import { useIntl } from 'react-intl'
import { LoanList } from '@matech/thebigpos-sdk'

import useUser from '../../../../hooks/useUser'
import formatLoanAmount from '../utils/formatLoanAmount'
import formatRoleSelector from '../utils/formatRoleSelector'
import { concatStrings } from '../../../../services/helpers'
import formatBorrowerAddress from '../utils/formatBorrowerAddress'
import RowExpandedProperty from '../../../../components/RowExpandedProperty'
import getBorrowerAndCoBorrowerUserFromLoanList from '../utils/getBorrowerAndCoBorrowerFromLoanList'

type PipelineExpandedRowProps = {
	data: LoanList
}

const PipelineExpandedRow = ({ data }: PipelineExpandedRowProps) => {
	const { user } = useUser()
	const { formatMessage } = useIntl()

	const {
		totalLoanAmount,
		loanNumber,
		status,
		buyerAgentContact,
		sellerAgentContact,
		loanOfficer,
		propertyAddress,
	} = data

	const { borrower, coBorrower } =
		getBorrowerAndCoBorrowerUserFromLoanList(data.userLoans)

	const borrowerName = concatStrings(
		borrower?.firstName,
		borrower?.lastName
	).getValue()

	const coBorrowerName = concatStrings(
		coBorrower?.firstName,
		coBorrower?.lastName
	).getValue()

	const formattedLoanAmountValue = formatLoanAmount(totalLoanAmount)

	const formattedRole = formatRoleSelector(
		user.email,
		sellerAgentContact?.email,
		buyerAgentContact?.email
	)

	const formattedAddress = formatBorrowerAddress(propertyAddress)

	const properties = [
		{
			labelKey: 'pipeline.expanded_row.borrower',
			value: borrowerName,
		},
		{
			labelKey: 'pipeline.expanded_row.co_borrower',
			value: coBorrowerName,
		},
		{
			labelKey: 'global.status',
			value: status,
		},
		{
			labelKey: 'global.role',
			value: formattedRole,
		},
		{
			labelKey: 'pipeline.columns.loan_amount',
			value: formattedLoanAmountValue,
		},
		{
			labelKey: 'pipeline.columns.loan_number',
			value: loanNumber,
		},
		{
			labelKey: 'loan_applications.loan_officer',
			value: loanOfficer?.name,
		},
		{
			labelKey: 'pipeline.columns.address',
			value: (
				<div className="min-w-0">
					{formattedAddress.map((line) => (
						<p
							key={line}
							className="truncate whitespace-normal font-rubik text-slate-800 font-bold mb-3 text-ellipsis overflow-hidden dark:text-white"
						>
							{line}
						</p>
					))}
				</div>
			),
		},
	]

	return (
		<div className="p-5 dark:bg-[#121212]">
			{properties.map((property) => {
				const label = formatMessage({ id: property.labelKey })
				const rowKey = label.toLowerCase().replace(' ', '-')

				return (
					<RowExpandedProperty
						key={rowKey}
						label={label}
						value={property.value}
					/>
				)
			})}
		</div>
	)
}

export default PipelineExpandedRow
